import { usePolling } from "./usePolling";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  RoomRestaurantOrderDto,
  listRoomRestaurantOrders,
} from "../utils/roomRestaurantOrdersApi";
import { listBuildings } from "../utils/buildingsApi";
import {
  getSeenRestaurantOrderIds,
  markRestaurantOrdersSeen,
  publishRestaurantOrderAlert,
} from "../utils/restaurantOrderAlert";

// Every open admin tab polls; usePolling skips hidden tabs and never overlaps
// requests. 15 s keeps alerts prompt without loading the server.
const POLL_INTERVAL_MS = 15_000;

/** Polls for open (Pending) restaurant orders every few seconds so the whole
 * admin console — sidebar badge plus the auto-popup — reacts to a just-
 * placed order quickly, without the Restaurant page itself needing to be
 * open. Mounted once, in AdminLayout. */
export const useRestaurantNewOrders = () => {
  const { user } = useAuth();
  const accountId = Number(user?.accountId) || 0;

  const [popupOrder, setPopupOrder] = useState<RoomRestaurantOrderDto | null>(
    null,
  );
  // Orders already known from a prior poll — only an order that's absent
  // from this set counts as having "just arrived" and is eligible to pop
  // up; on the very first poll after login every currently-pending order is
  // seeded in here so login doesn't trigger a popup storm, while still
  // correctly feeding the badge (which counts un-seen, not un-known).
  const knownIdsRef = useRef<Set<number> | null>(null);
  const poppedIdRef = useRef<number | null>(null);

  // Order DTOs only carry buildingId (see RoomRestaurantOrderDto) — the name
  // is resolved client-side against the account's buildings, same join the
  // Rooms admin screen already does, rather than the backend snapshotting a
  // building name onto every order row.
  const [buildingNameById, setBuildingNameById] = useState<
    Record<number, string>
  >({});

  useEffect(() => {
    if (!accountId) return;
    listBuildings()
      .then((all) => {
        const map: Record<number, string> = {};
        all.forEach((b) => {
          if (b.buildingName) map[b.buildingId] = b.buildingName;
        });
        setBuildingNameById(map);
      })
      .catch(() => {
        // Popup just won't show a building name.
      });
  }, [accountId]);

  const poll = useCallback(async () => {
    if (!accountId) return;
    try {
      const pending = await listRoomRestaurantOrders({
        accountId,
        orderStatus: "Pending",
      });

      const seen = getSeenRestaurantOrderIds();
      const unseenCount = pending.filter((o) => !seen.has(o.orderId)).length;
      publishRestaurantOrderAlert(unseenCount);

      const isFirstPoll = knownIdsRef.current === null;
      const known = knownIdsRef.current ?? new Set<number>();
      const arrivals = pending.filter(
        (o) => !known.has(o.orderId) && !seen.has(o.orderId),
      );
      knownIdsRef.current = new Set(pending.map((o) => o.orderId));

      if (!isFirstPoll && arrivals.length > 0) {
        const latest = arrivals.reduce((a, b) =>
          new Date(a.orderDate).getTime() >= new Date(b.orderDate).getTime()
            ? a
            : b,
        );
        if (poppedIdRef.current !== latest.orderId) {
          poppedIdRef.current = latest.orderId;
          setPopupOrder(latest);
        }
      }
    } catch {
      // Best-effort — a failed poll just tries again next tick.
    }
  }, [accountId]);

  usePolling(poll, POLL_INTERVAL_MS);

  const dismissPopup = useCallback(() => setPopupOrder(null), []);

  const acknowledgePopup = useCallback((orderId: number) => {
    markRestaurantOrdersSeen([orderId]);
    setPopupOrder((current) =>
      current?.orderId === orderId ? null : current,
    );
    poll();
  }, [poll]);

  const buildingName = popupOrder?.buildingId
    ? buildingNameById[popupOrder.buildingId] ?? null
    : null;

  return { popupOrder, buildingName, dismissPopup, acknowledgePopup };
};
